import React, { useEffect, useState } from "react";
import { get, ref } from "firebase/database";
import { database } from "../firebaseConfig";
import { dropoutRate } from "./outreachDropout";
import { buildDropoutBreakdown, hasStoredDropoutReasons } from "./outreachDropoutBreakdown";

const Share = ({ count, ofDropouts, ofTried }) => (
  <span className="oj-drop-share">
    <strong>{count}</strong>
    {` · ${ofDropouts}% of dropouts`}
    {ofTried > 0 ? ` · ${ofTried}% of contacts tried` : ""}
  </span>
);

const Bar = ({ pct }) => (
  <div className="oj-drop-bar" aria-hidden="true">
    <span style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} />
  </div>
);

/**
 * Click target for the Drop out stat. Opens the breakdown without also
 * opening the agent (the card itself is the navigation).
 */
export const DropoutStatCell = ({ drop, onOpen }) => {
  const openable = drop.dropped > 0;
  const health = drop.ready ? ` oj-stat-cell--${drop.health}` : "";
  const body = (
    <>
      <span className="oj-stat-val">{drop.ready ? `${drop.pct}%` : "–"}</span>
      <span className="oj-stat-lbl">Drop out</span>
      {drop.ready && (
        <span className="oj-stat-hint">{drop.dropped} of {drop.attempted}</span>
      )}
    </>
  );
  if (!openable) {
    return (
      <div
        className={`oj-stat-cell${health}`}
        title="Shows after a few contacts have been tried"
      >
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`oj-stat-cell oj-stat-cell--drop${health}`}
      title="See where these contacts left"
      aria-haspopup="dialog"
      aria-label={`Drop out ${drop.ready ? `${drop.pct} percent` : ""}, see where contacts left`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      {body}
    </button>
  );
};

const DropoutBreakdownModal = ({ userId, jobId, job, onClose }) => {
  const isPrepare = job?.deliveryMode === "prepare";
  const stats = job?.stats || {};
  const rate = dropoutRate(stats, { isPrepare });
  const [archive, setArchive] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setArchive(null);
    setError("");
    (async () => {
      try {
        const snap = await get(ref(database, `outreachJobArchive/${userId}/${jobId}`));
        if (cancelled) return;
        setArchive(snap.exists() ? snap.val() : {});
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Could not read the archive.");
        setArchive({});
      }
    })();
    return () => { cancelled = true; };
  }, [userId, jobId]);

  const loading = archive == null && !error;
  const breakdown = !error && archive
    ? buildDropoutBreakdown({
      steps: job?.steps || [],
      archive,
      storedReasons: hasStoredDropoutReasons(stats) ? stats.dropoutReasons : null,
      dropped: rate.dropped,
      attempted: rate.attempted,
      isPrepare,
    })
    : null;
  const name = job?.name || "This agent";

  return (
    <div className="oj-drop-overlay" onClick={onClose}>
      <div
        className="oj-drop-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="oj-drop-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="oj-drop-close" onClick={onClose} aria-label="Close">
          <i className="fa fa-times" aria-hidden="true" />
        </button>
        <p className="oj-drop-kicker">Drop out</p>
        <h3 id="oj-drop-title">{name}</h3>
        <p className={`oj-drop-pct${rate.health ? ` oj-drop-pct--${rate.health}` : ""}`}>
          {rate.pct == null ? "–" : `${rate.pct}%`}
          <span>{rate.dropped} of {rate.attempted} left before a mail was written</span>
        </p>

        {loading && <p className="oj-drop-copy">Reading where they left…</p>}
        {error && <p className="oj-drop-copy">{error}</p>}

        {breakdown && (
          <>
            {breakdown.lead && <p className="oj-drop-lead">{breakdown.lead}</p>}
            {breakdown.steps.length === 0 && !breakdown.unrecorded && (
              <p className="oj-drop-copy">No recorded exits yet.</p>
            )}
            <div className="oj-drop-list">
              {breakdown.steps.map((step) => (
                <section key={step.stepIndex} className="oj-drop-step">
                  <div className="oj-drop-step-head">
                    <span className="oj-drop-step-index">{step.stepIndex + 1}</span>
                    <span className="oj-drop-step-copy">
                      <span className="oj-drop-step-name">{step.title}</span>
                      <Share
                        count={step.count}
                        ofDropouts={step.shareOfDropouts}
                        ofTried={step.shareOfTried}
                      />
                    </span>
                  </div>
                  <Bar pct={step.shareOfDropouts} />
                  <ul className="oj-drop-reasons">
                    {step.reasons.map((reason) => (
                      <li key={reason.key}>
                        <span>{reason.label}</span>
                        <span className="oj-drop-reason-count">{reason.count}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
              {breakdown.unrecorded && (
                <section className="oj-drop-step oj-drop-step--unrecorded">
                  <div className="oj-drop-step-head">
                    <span className="oj-drop-step-index"><i className="fa fa-user-slash" aria-hidden="true" /></span>
                    <span className="oj-drop-step-copy">
                      <span className="oj-drop-step-name">No reason saved</span>
                      <Share
                        count={breakdown.unrecorded.count}
                        ofDropouts={breakdown.unrecorded.shareOfDropouts}
                        ofTried={breakdown.unrecorded.shareOfTried}
                      />
                    </span>
                  </div>
                  <Bar pct={breakdown.unrecorded.shareOfDropouts} />
                  <p className="oj-drop-note">
                    These were counted as dropouts, but no reason was saved, so they
                    cannot be split by step. They stay in the dropout number.
                    {breakdown.unrecorded.deletingSteps.length > 0 && (
                      <>
                        {" "}Steps that delete the contact used to drop the reason:{" "}
                        {breakdown.unrecorded.deletingSteps.map((step) => step.title).join(", ")}.
                      </>
                    )}
                  </p>
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DropoutBreakdownModal;
